import type {
  ColTypeDef,
  DataTypeDefinition,
  IDateFilterParams,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-community'
import toNumber from 'lodash/toNumber'
import { BasisPoints } from '@klear/basis-points'
import { formatBasisPoints } from '@klear/basis-points/boundary'
import { KlearDate } from '@klear/date'
import { klearDateFromJsDate } from '@klear/date/boundary'
import { Money } from '@klear/money'
import { Timestamp } from '@klear/time'
import { parseTimestamp } from '@klear/time/boundary'
import get from 'lodash/get'
import isDate from 'lodash/isDate'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'
import toString from 'lodash/toString'

function isIsoDateTimeString(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T/.test(value)
}

function asMoney(value: unknown): Money | null {
  return value instanceof Money ? value : null
}

function fieldValue(params: { data?: unknown; colDef: { field?: string } }): unknown {
  const field = params.colDef.field
  if (!field || params.data == null) return undefined
  return get(params.data, field)
}

export function cellValueFormatted(value: unknown): string {
  return value == null ? '-' : toString(value)
}

export function basisPointsCellBps(value: unknown): number | null {
  if (value == null) return null
  if (value instanceof BasisPoints) return value.valueOf()
  if (isNumber(value)) return value
  return null
}

export function basisPointsCellFormatted(value: unknown): string {
  return formatBasisPoints(value instanceof BasisPoints || isNumber(value) ? value : null, { digits: 2 })
}

export function klearDateCellYmd(value: unknown): string | null {
  if (value == null) return null
  if (KlearDate.is(value)) return value.toJSON()
  if (isString(value)) return KlearDate.parse(value)?.toJSON() ?? null
  if (value instanceof Timestamp) return value.toKlearDate().toJSON()
  if (isDate(value)) return Timestamp.fromEpochMs(value.getTime()).toKlearDate().toJSON()
  return null
}

export function klearDateCellFormatted(value: unknown): string {
  const ymd = isString(value) && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : klearDateCellYmd(value)
  return KlearDate.format(KlearDate.parse(ymd))
}

function resolveTimestamp(value: unknown): Timestamp | null {
  if (value == null) return null
  if (value instanceof Timestamp) return value
  if (isDate(value)) return Timestamp.fromEpochMs(value.getTime())
  if (isNumber(value)) return new Timestamp(value)
  if (isString(value)) return parseTimestamp(value) ?? null
  return null
}

export function timestampCellIso(value: unknown): string | null {
  return resolveTimestamp(value)?.toIso8601() ?? null
}

export function timestampCellFormatted(value: unknown): string {
  const ts = resolveTimestamp(value)
  if (!ts) return '-'
  return ts.toString('MM/DD/YYYY h:mm A')
}

export const KLEAR_DATE_FILTER_PARAMS: IDateFilterParams = {
  isValidDate: (value: unknown) => klearDateCellYmd(value) != null,
  comparator: (filterLocalDateAtMidnight, cellValue) => {
    const cellYmd = klearDateCellYmd(cellValue)
    const filterYmd = klearDateFromJsDate(filterLocalDateAtMidnight)?.toJSON()
    if (cellYmd == null || filterYmd == null) return -1
    if (cellYmd < filterYmd) return -1
    return cellYmd > filterYmd ? 1 : 0
  }
}

export const KLEAR_AG_GRID_COLUMN_TYPES: Record<string, ColTypeDef> = {
  text: {
    filter: 'agTextColumnFilter',
    sortable: true,
    valueFormatter: (params: ValueFormatterParams) => cellValueFormatted(params.value)
  },
  number: {
    filter: 'agNumberColumnFilter',
    sortable: true,
    valueFormatter: (params: ValueFormatterParams) => cellValueFormatted(params.value)
  },
  dateString: {
    filter: 'agDateColumnFilter',
    filterParams: KLEAR_DATE_FILTER_PARAMS,
    sortable: true,
    valueFormatter: (params: ValueFormatterParams) => klearDateCellFormatted(fieldValue(params) ?? params.value)
  },
  money: {
    filter: 'agNumberColumnFilter',
    sortable: true,
    valueGetter: (params: ValueGetterParams) => asMoney(fieldValue(params)),
    valueFormatter: (params: ValueFormatterParams) => asMoney(params.value ?? fieldValue(params))?.toString() ?? '-',
    comparator: (a: Money | null, b: Money | null) => {
      if (a == null) return b == null ? 0 : -1
      if (b == null) return 1
      return a.compare(b)
    },
    filterValueGetter: (params) => {
      const money = asMoney(fieldValue(params))
      return money != null ? toNumber(money.amount.toString()) : null
    }
  },
  basisPoints: {
    filter: 'agNumberColumnFilter',
    sortable: true,
    valueGetter: (params: ValueGetterParams) => basisPointsCellBps(fieldValue(params)),
    valueFormatter: (params: ValueFormatterParams) => basisPointsCellFormatted(params.value ?? fieldValue(params)),
    filterValueGetter: (params) => basisPointsCellBps(fieldValue(params))
  },
  klearDate: {
    filter: 'agDateColumnFilter',
    filterParams: KLEAR_DATE_FILTER_PARAMS,
    sortable: true,
    valueGetter: (params: ValueGetterParams) => klearDateCellYmd(fieldValue(params)),
    valueFormatter: (params: ValueFormatterParams) => klearDateCellFormatted(fieldValue(params) ?? params.value)
  },
  timestamp: {
    filter: 'agDateColumnFilter',
    filterParams: KLEAR_DATE_FILTER_PARAMS,
    sortable: true,
    valueGetter: (params: ValueGetterParams) => timestampCellIso(fieldValue(params)),
    valueFormatter: (params: ValueFormatterParams) => timestampCellFormatted(fieldValue(params) ?? params.value)
  },
  actions: {
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    resizable: false
  }
}

export const KLEAR_AG_GRID_DATA_TYPE_DEFINITIONS: {
  [cellDataType: string]: DataTypeDefinition
} = {
  money: {
    baseDataType: 'object',
    extendsDataType: 'object',
    suppressDefaultProperties: true,
    dataTypeMatcher: (value) => value instanceof Money,
    valueFormatter: (params) => asMoney(params.value)?.toString() ?? '-',
    columnTypes: 'money'
  },
  basisPoints: {
    baseDataType: 'object',
    extendsDataType: 'object',
    suppressDefaultProperties: true,
    dataTypeMatcher: (value) => value instanceof BasisPoints,
    valueFormatter: (params) => basisPointsCellFormatted(params.value),
    columnTypes: 'basisPoints'
  },
  klearDate: {
    baseDataType: 'object',
    extendsDataType: 'object',
    suppressDefaultProperties: true,
    dataTypeMatcher: (value) => KlearDate.is(value),
    valueFormatter: (params) => klearDateCellFormatted(params.value),
    columnTypes: 'klearDate'
  },
  timestamp: {
    baseDataType: 'object',
    extendsDataType: 'object',
    suppressDefaultProperties: true,
    dataTypeMatcher: (value) => value instanceof Timestamp || (isString(value) && isIsoDateTimeString(value)),
    valueFormatter: (params) => timestampCellFormatted(params.value),
    columnTypes: 'timestamp'
  },
  text: {
    baseDataType: 'text',
    extendsDataType: 'text',
    valueFormatter: (params) => cellValueFormatted(params.value),
    columnTypes: 'text'
  },
  number: {
    baseDataType: 'number',
    extendsDataType: 'number',
    valueFormatter: (params) => cellValueFormatted(params.value),
    columnTypes: 'number'
  },
  dateString: {
    baseDataType: 'dateString',
    extendsDataType: 'dateString',
    valueFormatter: (params) => klearDateCellFormatted(params.value),
    columnTypes: 'dateString'
  }
}

export const KLEAR_AG_GRID_DEFAULT_COL_DEF = {
  resizable: true,
  sortable: true,
  filter: true,
  valueFormatter: (params: ValueFormatterParams) => cellValueFormatted(params.value)
} as const
